import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Plus, Info, Loader2 } from 'lucide-react';
import { connect } from 'react-redux';
import { getAllUsersBySkills, createNewSkill, openMainModal, closeMainModal } from '../../../store/actions/profileActions';
import { RubroOption } from '../../../types/newProject';
import styles from './RubroSelector.module.scss';

interface RubroSelectorProps {
  onRubroSelect: (rubroId: number, rubroName: string) => void;
  existingRubros: Array<{ name: string; id?: string }>;
  selectedRubro?: string;
  onClear?: () => void;
  disabled?: boolean;
  // Redux props
  skillsList: any[];
  newSkill: any;
  getAllUsersBySkills: () => void;
  createNewSkill: (skillName: string, isNew: boolean) => void;
  openMainModal: (modalData: any) => void;
  closeMainModal: () => void;
}

const RubroSelector: React.FC<RubroSelectorProps> = ({
  onRubroSelect,
  existingRubros,
  selectedRubro = "",
  onClear,
  disabled = false,
  skillsList,
  newSkill,
  getAllUsersBySkills,
  createNewSkill,
  openMainModal,
  closeMainModal
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showCompleteList, setShowCompleteList] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cargar rubros disponibles al montar el componente
  useEffect(() => {
    getAllUsersBySkills();
  }, [getAllUsersBySkills]);

  // Función de validación mejorada
  const validateInput = useCallback((value: string) => {
    const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;
    
    if (value.length > 0 && value.length < 3) {
      setIsValid(false);
      setValidationMessage('Mínimo 3 caracteres');
    } else if (value.length >= 3 && !regex.test(value)) {
      setIsValid(false);
      setValidationMessage('Solo letras permitidas');
    } else {
      setIsValid(true);
      setValidationMessage('');
    }
  }, []);

  // Manejar nuevo rubro creado (usando Redux state)
  useEffect(() => {
    if (newSkill && newSkill.id && newSkill.name) {
      onRubroSelect(newSkill.id, newSkill.name);
      setShowSuggestions(false);
      setInputValue("");
      setShowCompleteList(false);
    }
  }, [newSkill, onRubroSelect]);


  const normalizeString = (str: string): string => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  };

  const handleInputChange = useCallback((value: string) => {
    validateInput(value);
    
    if (value.length < 3) {
      setShowSuggestions(false);
    } else if (value.length >= 3) {
      setShowSuggestions(true);
    }
    
    setInputValue(value);
  }, [validateInput]);

  const filteredSuggestions = skillsList.filter((rubro) => {
    return rubro.name.toLowerCase().includes(inputValue.toLowerCase());
  });

  const handleRubroSelect = useCallback((rubro: RubroOption) => {
    // Verificar si ya existe
    if (existingRubros.some((existingRubro) => existingRubro.name === rubro.name)) {
      openMainModal({
        title: `Ups, ya tienes un rubro con el nombre ${rubro.name}`,
        iconType: "error",
        body: `Si deseas editarlo o eliminarlo, puedes hacerlo desde la lista de "Agregados".`,
        isSuccess: true,
        confirmHandler: null,
      });
      return;
    }

    // Selección directa sin modal de confirmación
    onRubroSelect(rubro.id, rubro.name);
    setShowSuggestions(false);
    setInputValue("");
    setShowCompleteList(false);
  }, [existingRubros, onRubroSelect, openMainModal]);

  const handleCreateRubro = useCallback(async () => {
    if (inputValue.length >= 3 && isValid) {
      try {
        setIsCreating(true);
        
        // Verificar si ya existe un rubro con ese nombre normalizado
        const normalizedInputValue = normalizeString(inputValue);
        const matchedRubro = skillsList.find((rubro) => 
          normalizeString(rubro.name) === normalizedInputValue
        );

        if (!matchedRubro) {
          // Crear nuevo rubro usando Redux action
          createNewSkill(inputValue, true);
          
          // Actualizar lista después de 2 segundos (misma lógica del flujo anterior)
          setTimeout(() => {
            getAllUsersBySkills();
          }, 2000);
        } else {
          // Si ya existe, seleccionarlo
          handleRubroSelect(matchedRubro);
        }
      } catch (error) {
        console.error('Error creando nuevo rubro:', error);
      } finally {
        setIsCreating(false);
      }
    }
  }, [inputValue, isValid, skillsList, handleRubroSelect, createNewSkill, getAllUsersBySkills]);

  const handleCancel = useCallback(() => {
    setInputValue("");
    setShowSuggestions(false);
    setShowCompleteList(false);
    onClear?.();
  }, [onClear]);

  // Manejar click fuera del componente
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Verificar si el click fue en cualquier elemento del RubroSelector
      if (containerRef.current && !containerRef.current.contains(target)) {
        setShowSuggestions(false);
        setShowCompleteList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.rubroSelector}>
      {selectedRubro !== "" ? (
        <div className={styles.selectedRubro}>
          <div className={styles.selectedRubroLabel}>
            {selectedRubro}
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={disabled}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <Search size={18} className={styles.searchIcon} />
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={() => {
                  if (inputValue.length >= 3) {
                    setShowSuggestions(true);
                  }
                }}
                placeholder="Ingresa al menos 3 caracteres. Si no encuentras el rubro, puedes crearlo"
                className={`${styles.input} ${!isValid ? styles.inputError : ''}`}
                disabled={disabled || isLoading || isCreating}
              />
            </div>
            
            <button
              type="button"
              className={styles.showAllButton}
              onClick={() => setShowCompleteList(!showCompleteList)}
              disabled={disabled || isLoading || isCreating}
            >
              {showCompleteList ? 'Ocultar' : 'Ver todos'}
            </button>
          </div>

          {/* Mensaje de validación */}
          {!isValid && validationMessage && (
            <div className={styles.validationMessage}>
              <Info size={14} />
              {validationMessage}
            </div>
          )}

          {/* Lista completa */}
          {showCompleteList && (
            <div className={styles.suggestions}>
              {isLoading ? (
                <div className={styles.loadingItem}>Cargando rubros...</div>
              ) : (
                skillsList.map((rubro) => (
                  <div
                    key={rubro.id}
                    className={styles.suggestionItem}
                    onClick={() => handleRubroSelect(rubro)}
                  >
                    {rubro.name}
                    <span className={styles.userCount}>
                      - hay {rubro.users} {rubro.users === 1 ? "profesional" : "profesionales"} en edify
                    </span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Sugerencias filtradas */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className={styles.suggestions}>
              {filteredSuggestions.map((rubro) => (
                <div
                  key={rubro.id}
                  className={styles.suggestionItem}
                  onClick={() => handleRubroSelect(rubro)}
                >
                  {rubro.name}
                  <span className={styles.userCount}>
                    - hay {rubro.users} {rubro.users === 1 ? "profesional" : "profesionales"} en edify
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Mensaje de error de validación */}
          {showSuggestions && !isValid && (
            <div className={styles.errorItem}>Ingrese solo letras</div>
          )}

          {/* Botón para crear nuevo rubro */}
          {showSuggestions && filteredSuggestions.length === 0 && isValid && (
            <div className={styles.createNewRubro}>
              <div className={styles.createNewRubroInfo}>
                <Info size={16} />
                <span>No hay rubros que coincidan con tu búsqueda. ¿Quieres crear "{inputValue}"? El equipo de edify te ayudará a encontrar profesionales.</span>
              </div>
              <button
                type="button"
                className={styles.createButton}
                onClick={handleCreateRubro}
                disabled={disabled || isLoading || isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 size={16} className={styles.spinner} />
                    Creando...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Crear "{inputValue}"
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    skillsList: state.register.skillsList || [],
    newSkill: state.profile.newSkill || null
  };
};

export default connect(mapStateToProps, {
  getAllUsersBySkills,
  createNewSkill,
  openMainModal,
  closeMainModal
})(RubroSelector);
